import api from "@/libs/axios";
import { QueryConfig } from "@/libs/query";
import { infiniteQueryOptions, useInfiniteQuery } from "@tanstack/react-query";
import { apiEnum } from "@/pages/taskpane/shared/types/enums/apiEnum";
import { DeskLocationEntity, ResourceEntity } from "./types/resourceEntity";
import { QUERY_KEY } from "@/pages/taskpane/shared/constants/queryKey";

type PayLoadType = {
    pageSize?: number;
    nextIndex?: number;
    organizer?: string;
    locationIds?: number[];
    resourceType?: string;
    timeZoneStandardName?: string;
    occurrences?: {
        utcStartDate: string;
        utcEndDate: string;
    }[];
    capacity?: 0;
    attendees?: string[];
    isNoEndDate?: boolean | null;
    isRecurring?: boolean;
    categoryIds?: number[];
};

const getResourcesRecommended = async (payload: PayLoadType) => {
    const { data } = await api.post(apiEnum.getResourcesRecommended, payload);
    return {
        isLastPage: data?.data?.isLastPage || false,
        nextIndex: data?.data?.nextIndex || 0,
        deskLocations: (data?.data?.deskLocations || []).map((item: any) => new DeskLocationEntity(item)),
        resources: (data?.data?.resources || []).map((item: any) => new ResourceEntity(item)),
    };
};

export const options = (payload: PayLoadType) => infiniteQueryOptions({
    queryKey: [
        QUERY_KEY.GET_RESOURCES,
        QUERY_KEY.GET_RESOURCES_RECOMMENDED,
        {
            organizer: payload.organizer || '',
            resourceType: payload.resourceType || '',
            locationIds: payload.locationIds?.sort().join(',') || '',
            timeZone: payload.timeZoneStandardName || '',
            // Hash occurrences - include full datetime for accurate cache key
            // Each time change creates a new query (expected behavior for resource availability)
            occurrencesHash: JSON.stringify(payload.occurrences?.map(o => ({
                start: o.utcStartDate,
                end: o.utcEndDate
            })) || []),
            capacity: payload.capacity || 0,
            attendeesHash: payload.attendees?.sort().join(',') || '',
            isNoEndDate: payload.isNoEndDate || false,
            isRecurring: payload?.isRecurring || false,
            categoryIds: payload?.categoryIds?.sort().join(',') || '',
        }
    ],
    queryFn: ({ pageParam }) => {
        // console.log("🔄 Fetching resources - Page:", pageParam, "Params:", {
        //     resourceType: payload.resourceType,
        //     occurrences: payload.occurrences,
        //     timeZone: payload.timeZoneStandardName
        // });
        return getResourcesRecommended({ ...payload, nextIndex: pageParam });
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
        if (lastPage?.isLastPage) {
            // console.log("✅ Reached last page:", allPages.length);
            return undefined;
        }
        // console.log("📄 Loading next page:", allPages.length + 1);
        return allPages[allPages.length - 1]?.nextIndex || 0;
    },
    // ✅ Smart caching strategy for best UX
    // staleTime: 2 * 60 * 1000, // Cache fresh for 2 minutes (reduce API calls on quick switches)
    // gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes after unmount
    // refetchOnMount: false, // Don't refetch if cache is still fresh (within staleTime)
    // refetchOnWindowFocus: false, // Don't refetch on window focus (avoid too many requests)
})

type GetResourcesRecommendedType = {
    queryConfig?: QueryConfig<typeof options>;
    payload: PayLoadType;
};

export const useGetResourcesRecommended = ({ queryConfig, payload }: GetResourcesRecommendedType) => {
    const query = useInfiniteQuery({
        ...options(payload),
        ...queryConfig,
    });

    // Debug: Log cache status
    // console.log("📦 Query status:", {
    //     status: query.status,
    //     isFetching: query.isFetching,
    //     dataUpdatedAt: query.dataUpdatedAt ? new Date(query.dataUpdatedAt).toLocaleTimeString() : 'never',
    //     fetchStatus: query.fetchStatus,
    //     occurrences: payload.occurrences
    // });

    return query;
};
